import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * Gate a route on being signed in, and on the role being allowed.
 *
 * ALWAYS CARRIES WHERE YOU WERE GOING (2026-08-28). This used to send everyone
 * to a bare /login and forget the destination — so a link to a booking, an
 * invoice or a pay page dropped you on the home page after signing in, and you
 * had to find your way back to whatever the link was for. The single exception
 * was the payment-return round trip, which had to carry `next` or a verified
 * payment outcome was lost; the rest of the app deserved the same treatment.
 *
 * `next` is read by Login.safeNextUrl, which accepts SAME-ORIGIN RELATIVE
 * PATHS ONLY — an absolute URL or a protocol-relative //host is ignored, so
 * this can never be used to bounce somebody off the portal onto another site.
 * The query string rides along, because for a lot of these pages (a payment
 * return, a filtered list) the parameters are the destination.
 *
 * A WRONG ROLE IS NOT A MISSING SESSION. Somebody signed in who simply may not
 * see this page goes home, not to a login form — asking them to sign in again
 * implies the credentials were the problem, and they will end up in the same
 * place a second time. `/` re-routes clients to their own home.
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
  const userRole = localStorage.getItem('userRole'); // stored during login

  if (!userRole) {
    const { pathname, search, hash } = window.location;
    const target = `${pathname}${search}${hash}`;
    // Never round-trip back to the login screen itself.
    const next = pathname.startsWith('/login')
      ? ''
      : `?next=${encodeURIComponent(target)}`;
    return <Navigate to={`/login${next}`} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
